import React, { useEffect, useState } from "react";
import { Printer, ShieldAlert, Globe, FileWarning, KeyRound, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SIG = { Critical: "#A5300F", Notable: "#E39629", Informational: "#7F5F52" };
const Pill = ({ s }) => (
  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
    style={{ color: "#fff", backgroundColor: SIG[s] || "#8A8175" }}>{s}</span>
);
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

function Section({ icon: Icon, title, count, children, testid }) {
  return (
    <section className="mb-8 break-inside-avoid" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 border-b-2 border-[#A5300F] pb-1">
        <Icon className="w-4 h-4 text-[#A5300F]" />
        <h2 className="font-display text-lg font-bold text-[#2B2622]">{title}</h2>
        <span className="ml-auto text-sm text-[#8A8175]">{count} item{count === 1 ? "" : "s"}</span>
      </div>
      {count === 0 ? <p className="text-sm text-[#8A8175] italic py-2">No signals in this category.</p> : children}
    </section>
  );
}

const th = "text-left font-semibold text-[#8A8175] uppercase tracking-wider text-[10px] py-1.5 px-2";
const td = "py-1.5 px-2 align-top border-t border-[#F0EAE1]";

export default function ExposureReport() {
  const { ready, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const orgId = typeof window !== "undefined" ? localStorage.getItem("cl_org") : null;

  useEffect(() => {
    if (!ready || !user || !orgId) return;
    http.get("/monitoring/exposure-report", { params: { org_id: orgId } })
      .then((r) => setData(r.data)).catch(() => setErr(true));
  }, [ready, user, orgId]);

  if (!ready) return <div className="p-10 text-center text-[#8A8175]">Loading…</div>;
  if (!user) { navigate("/login"); return null; }
  if (err) return <div className="p-10 text-center text-[#A5300F]">Failed to load report.</div>;
  if (!data) return <div className="p-10 text-center text-[#8A8175]">Generating report…</div>;

  const s = data.summary;
  return (
    <div className="min-h-screen bg-[#FBF7F1] text-[#2B2622]" data-testid="exposure-report">
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .report-sheet { box-shadow:none !important; margin:0 !important; max-width:none !important; } }`}</style>

      <div className="no-print sticky top-0 z-10 bg-[#2B2622] text-white px-6 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/monitoring/impersonation")} className="flex items-center gap-1 text-sm text-[#E8DFD3] hover:text-white" data-testid="report-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-sm text-[#B5AC9E]">External Exposure Report</span>
        <button onClick={() => window.print()} data-testid="report-print"
          className="ml-auto flex items-center gap-2 bg-[#A5300F] hover:bg-[#8A2708] text-white text-sm font-semibold rounded-full px-4 py-1.5">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="report-sheet max-w-[900px] mx-auto bg-white my-6 p-10 shadow-sm">
        <div className="flex items-start justify-between border-b border-[#ECE6DD] pb-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#A5300F] font-bold">CyberLens · Confidential</div>
            <h1 className="font-display text-3xl font-bold mt-1">External Exposure Report</h1>
            <div className="text-[#5C554C] mt-1">{data.org.name}</div>
          </div>
          <div className="text-right text-xs text-[#8A8175]">
            <div>Generated</div>
            <div className="font-semibold text-[#2B2622]">{new Date(data.generated_at).toLocaleString("en-GB")}</div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-8">
          {[["Lookalike Domains", s.lookalikes, Globe], ["CT Exposure", s.ct, ShieldAlert],
            ["Public Code Leaks", s.github, FileWarning], [`Certs Expiring ≤${data.expiry_window_days}d`, s.expiring_certs, KeyRound]].map(([lbl, n, Icon]) => (
            <div key={lbl} className="border border-[#ECE6DD] rounded-lg p-3">
              <Icon className="w-4 h-4 text-[#A5300F]" />
              <div className="stat-numeral text-2xl mt-1">{n}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#8A8175] leading-tight mt-0.5">{lbl}</div>
            </div>
          ))}
        </div>

        <Section icon={Globe} title="Lookalike Domains" count={s.lookalikes} testid="rep-lookalikes">
          <table className="w-full text-sm"><thead><tr>
            <th className={th}>Domain</th><th className={th}>Age</th><th className={th}>Mail</th><th className={th}>Registrar</th><th className={th}>Severity</th><th className={th}>Disposition</th><th className={th}>Detected</th>
          </tr></thead><tbody>
            {data.lookalikes.map((l, i) => (
              <tr key={`${l.fqdn}-${i}`}>
                <td className={`${td} font-mono font-semibold`}>{l.fqdn}</td>
                <td className={td}>{l.age_days != null ? `${l.age_days}d` : "—"}</td>
                <td className={td}>{l.mx ? "Yes" : "—"}</td>
                <td className={td}>{(l.registrar || "—").slice(0, 30)}</td>
                <td className={td}><Pill s={l.significance} /></td>
                <td className={td}>{l.disposition || "—"}</td>
                <td className={td}>{fmt(l.detected_at)}</td>
              </tr>
            ))}
          </tbody></table>
        </Section>

        <Section icon={ShieldAlert} title="Certificate Transparency Exposure" count={s.ct} testid="rep-ct">
          <table className="w-full text-sm"><thead><tr>
            <th className={th}>Domain</th><th className={th}>Type</th><th className={th}>Detail</th><th className={th}>Severity</th><th className={th}>Detected</th>
          </tr></thead><tbody>
            {data.ct.map((c, i) => (
              <tr key={`${c.domain}-${i}`}>
                <td className={`${td} font-mono`}>{c.domain}</td>
                <td className={td}>{c.type === "ct_new_issuer" ? "New issuer" : "New subdomain"}</td>
                <td className={`${td} break-all`}>{typeof c.detail === "string" ? c.detail : JSON.stringify(c.detail)}</td>
                <td className={td}><Pill s={c.significance} /></td>
                <td className={td}>{fmt(c.detected_at)}</td>
              </tr>
            ))}
          </tbody></table>
        </Section>

        <Section icon={FileWarning} title="Public Code Leaks (GitHub)" count={s.github} testid="rep-github">
          <div className="space-y-3">
            {data.github.map((g, i) => (
              <div key={`${g.url}-${i}`} className="border border-[#F0EAE1] rounded p-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill s={g.significance} />
                  <a href={g.url} target="_blank" rel="noreferrer" className="text-[#A5300F] underline text-sm font-mono break-all">{g.repo}{g.path ? `/${g.path}` : ""}</a>
                  <span className="ml-auto text-[11px] text-[#8A8175]">{fmt(g.detected_at)}</span>
                </div>
                {g.snippet && <pre className="mt-1.5 text-[11px] bg-[#2B2622] text-[#E8DFD3] rounded p-2 overflow-x-auto whitespace-pre-wrap break-all font-mono">{g.snippet}</pre>}
              </div>
            ))}
          </div>
        </Section>

        <Section icon={KeyRound} title={`Expiring Certificates (≤ ${data.expiry_window_days} days)`} count={s.expiring_certs} testid="rep-certs">
          <table className="w-full text-sm"><thead><tr>
            <th className={th}>Domain</th><th className={th}>Common name</th><th className={th}>Issuer</th><th className={th}>Expires</th>
          </tr></thead><tbody>
            {data.expiring_certs.map((c, i) => (
              <tr key={`${c.common_name}-${i}`}>
                <td className={`${td} font-mono`}>{c.domain}</td>
                <td className={`${td} font-mono`}>{c.common_name}</td>
                <td className={td}>{c.issuer}</td>
                <td className={td}><span style={{ color: c.days != null && c.days < 14 ? "#A5300F" : c.days != null && c.days <= 30 ? "#E39629" : "#5C554C", fontWeight: 600 }}>{fmt(c.not_after)}{c.days != null ? ` · ${c.days < 0 ? "expired" : c.days + "d"}` : ""}</span></td>
              </tr>
            ))}
          </tbody></table>
        </Section>

        <div className="text-[10px] text-[#B5AC9E] border-t border-[#ECE6DD] pt-3 mt-6">
          Generated by CyberLens Continuous Monitoring. This report lists open external-exposure signals at time of generation and is intended for the named organisation only.
        </div>
      </div>
    </div>
  );
}
